import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/feature/JsonLd';
import { articleSchema, articleFaqSchema } from '@/data/schema';
import { articles } from '@/data/articles';

export default function Article() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="mx-auto max-w-3xl px-4 md:px-6 py-20 text-center">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">{t('article_not_found')}</h1>
          <Link to="/" className="mt-6 inline-block rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 hover:bg-primary-600 whitespace-nowrap transition-colors cursor-pointer">
            {t('back_home')}
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={articleSchema(article)} />
      <JsonLd data={articleFaqSchema(article)} />
      <Navbar />
      <main>
        <article className="mx-auto max-w-3xl px-4 md:px-6 py-12 md:py-16">
          <nav aria-label="Breadcrumb" className="mb-8 text-sm text-foreground-500">
            <ol className="flex flex-wrap items-center gap-1">
              <li>
                <Link to="/" className="hover:text-primary-600 cursor-pointer">{t('nav_home')}</Link>
              </li>
              <li aria-hidden="true">/</li>
              <li aria-current="page" className="text-foreground-700 font-medium">{article.category}</li>
              <li aria-hidden="true">/</li>
              <li aria-current="page" className="text-foreground-700 font-medium">{article.title}</li>
            </ol>
          </nav>

          <header>
            <span className="inline-flex items-center gap-2 rounded-full bg-primary-100/80 px-4 py-1.5 text-xs font-semibold text-primary-700">
              <i className="ri-article-line" aria-hidden="true"></i>
              {article.category}
            </span>
            <h1 className="mt-4 text-3xl md:text-4xl font-bold leading-tight text-foreground-950">{article.title}</h1>
          </header>

          <div className="mt-6 rounded-lg border border-primary-200 bg-primary-50 p-5">
            <p className="text-sm font-semibold uppercase tracking-wide text-primary-700">{t('quick_answer_label')}</p>
            <p className="mt-2 text-base leading-relaxed text-foreground-800">{article.quickAnswer}</p>
          </div>

          <p className="mt-8 text-base md:text-lg leading-relaxed text-foreground-600">{article.intro}</p>

          {article.blocks.map((block, index) => {
            if (block.type === 'h2') {
              return (
                <section key={index} className="mt-10">
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-950">{block.text}</h2>
                </section>
              );
            }
            if (block.type === 'h3') {
              return (
                <h3 key={index} className="mt-8 text-xl font-semibold text-foreground-900">{block.text}</h3>
              );
            }
            if (block.type === 'list') {
              return (
                <ul key={index} className="mt-5 space-y-3">
                  {block.items.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm leading-relaxed text-foreground-700">
                      <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-accent-100 text-accent-600">
                        <i className="ri-check-line" aria-hidden="true"></i>
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
              );
            }
            return (
              <p key={index} className="mt-4 text-base leading-relaxed text-foreground-600">{block.text}</p>
            );
          })}

          <section className="mt-12">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950">{t('faq_title')}</h2>
            <div className="mt-6 space-y-3">
              {article.faq.map((item, index) => {
                const isOpen = openFaq === index;
                return (
                  <div key={item.q} className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setOpenFaq(isOpen ? null : index)}
                      className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left cursor-pointer"
                    >
                      <span className="text-sm md:text-base font-semibold text-foreground-900">{item.q}</span>
                      <i className={`ri-arrow-down-s-line text-lg text-foreground-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true"></i>
                    </button>
                    {isOpen && (
                      <div className="px-5 pb-4 text-sm leading-relaxed text-foreground-600">{item.a}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          <section className="mt-12 rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
            <h2 className="text-xl font-bold text-foreground-950">{t('related_label')}</h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">{t('related_text')}</p>
            <div className="mt-5 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/"
                className="rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 hover:bg-primary-600 whitespace-nowrap transition-colors text-center cursor-pointer"
              >
                {t('back_home')}
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="rounded-full border border-background-300 bg-background-50 px-6 py-3 text-sm font-semibold text-foreground-700 hover:bg-background-200 whitespace-nowrap transition-colors text-center cursor-pointer"
              >
                {t('footer_visit_main')}
              </a>
            </div>
          </section>
        </article>
      </main>
      <Footer />
    </div>
  );
}